import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { Card, CardActionArea, CardContent, IconButton, Stack, Tooltip } from '@mui/material'
import * as React from 'react'

import CustomTypography from '@/components/dataDisplay/CustomTypography'

const cardStyles = {
  height: '100%',
  display: 'flex',
  flex: 1,
}

interface NumericWidgetProps {
  title: string
  disabled?: boolean
  value?: number | string
  icon?: string | React.ReactElement
  onClick?: () => void
  valueFontSize?: number
  color?: 'neutral' | 'positive' | 'negative'
  tooltip?: string
}

export default function NumericWidget({
  title,
  disabled,
  value,
  icon,
  onClick,
  valueFontSize = 18,
  color = 'neutral',
  tooltip,
}: Readonly<NumericWidgetProps>) {
  return (
    <Card sx={cardStyles} onClick={onClick}>
      <CardActionArea disabled={disabled}>
        <CardContent>
          <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
            <CustomTypography variant={'h4'}>{title}</CustomTypography>
            <Stack alignItems={'center'} direction={'row'} spacing={1}>
              {tooltip && (
                <Tooltip title={tooltip}>
                  {/* The icon is a focusable, labelled button so keyboard and screen-reader users can
                      reach the explanation too. pointerEvents must be re-enabled because a disabled
                      action area suppresses them on its subtree, and clicks must not bubble to the
                      card's onClick when the widget is clickable. */}
                  <IconButton
                    aria-label={tooltip}
                    size="small"
                    sx={{ color: 'grey.500', padding: 0, pointerEvents: 'auto' }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!disabled && <KeyboardArrowRightIcon color="primary" fontSize="small" />}
            </Stack>
          </Stack>
          <Stack alignContent={'center'} alignItems={'center'} direction={'row'} spacing={1} sx={{ mt: 2, mb: 1 }}>
            {icon}
            <CustomTypography
              color={getTextColor(color)}
              fontSize={valueFontSize}
              fontWeight={'bold'}
              letterSpacing={0}
              variant={'h4'}
            >
              {value}
            </CustomTypography>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

function getTextColor(color: 'neutral' | 'positive' | 'negative') {
  switch (color) {
    case 'positive':
      return 'success'
    case 'negative':
      return 'error'
    default:
      return 'textPrimary'
  }
}
